import { Component, OnInit, Input, ErrorHandler, Output, EventEmitter } from '@angular/core';
import { Review } from '../models/review.model';
import { ReviewSummary } from '../models/review-summary.model';
import { WriteReviewComponent } from '../write-review/write-review.component';
import { MatDialog } from '@angular/material';
import { Airport } from '../../airport/models/airport.model';
import { Business } from '../../business/models/business.model';
import { AuthService } from '../../auth/auth/auth.service';
import { UserService } from '../user/user.service';
import { UserInfo } from '../models/user-info.model';
import { hasTag } from '../../util/tag-utils';
import { EditToggleService } from '../services/edit-toggle.service';
import { AirportService } from '../../airport/airport.service';
import { State } from '../state-view/state.model';
import { MatSnackBar } from '@angular/material';
import { BusinessManagerService } from '../user/business-manager.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { EditReviewResponseComponent } from '../edit-review-response/edit-review-response.component';
import { NgxGalleryOptions, NgxGalleryImage, NgxGalleryAnimation } from 'ngx-gallery';
import { ReviewReportOffensiveComponent } from '../review-report-offensive/review-report-offensive.component';
import { ReviewMoveComponent } from '../review-move/review-move.component';
import { Router } from '../../../../node_modules/@angular/router';


@Component({
  selector: 'app-reviews',
  templateUrl: './reviews.component.html',
  styleUrls: ['./reviews.component.scss']
})
export class ReviewsComponent implements OnInit {
  @Input() airport: Airport;
  @Input() business: Business;
  @Output() reloadThisEntity = new EventEmitter<boolean>();
  userReview: Review;
  private _userInfo: UserInfo;
  isFlyingClub: boolean;
  editModeSet = false;
  state = State.Empty;
  isFlightDeckAdmin = false;
  sortBy = 'likes';
  recId: number;
  isBusinessManager = false;
  isAirportManager = false;
  galleryOptions: NgxGalleryOptions[];
  galleryImages: NgxGalleryImage[];
  newPhotoIds: Array<number> = [];

  constructor(private _toggleService: EditToggleService,
              public dialog: MatDialog,
              private _auth: AuthService,
              private _userService: UserService,
              private _airportService: AirportService,
              private _errorHandler: ErrorHandler,
              private _snackBar: MatSnackBar,
              private _businessManagerService: BusinessManagerService,
              private _router: Router) {
    this._toggleService.toggleEmitter.subscribe(isOn => {
      this.editModeSet = isOn;
    });
    this.galleryOptions = [
      {
        width: '341px',
        // height: '255px', // for block with image preview
        height: '80px',
        thumbnailsColumns: 3,
        image: false, // remove to have image preview
        imageSize: 'contain',
        //  imageAnimation: NgxGalleryAnimation.Slide
        thumbnailsMargin: 5,
        thumbnailMargin: 5,
        imageArrowsAutoHide: true,
        thumbnailsArrowsAutoHide: true,
        previewCloseOnClick: true,
        previewCloseOnEsc: true
      }];
  }

  async ngOnInit() {
    // Update state based on latest available userInfo, and subscribe to any future
    // changes to userInfo.
    this._userInfo = this._userService.userInfo.getValue();
    if (this._userInfo) {
      this.recId = Number(this._userInfo.masterCustomerId);
    }
    this._userService.userInfo.subscribe(u => {
      // Update userInfo
      this._userInfo = u;
      if (u) {
        this.recId = Number(u.masterCustomerId);
      }
    });
    if (this.business) {
      this.isFlyingClub = hasTag(this.business.tags, 'Flying Club');
      this.isBusinessManager = await this._businessManagerService.userOwnsBusiness(this.business.id);
    } else if (this.airport) {
      this.isAirportManager = await this._businessManagerService.userManagesAirport(this.airport.airportId);
    }
    this.isFlightDeckAdmin = await this._businessManagerService.isFlightDeckAdmin();
  }

  openWriteReview() {
    if (!this._auth.authenticated || !this._auth.endUser) {
      this._auth.redirectToLogin();
      return;
    }

    this.userReview = this.reviews.find(review => review.userId === Number(this._userInfo.masterCustomerId));

    const dialogRef = this.dialog.open(WriteReviewComponent, {
     // height: '500px',
      data: { airport: this.airport, business: this.business, reviews: this.reviews, userReview: this.userReview,
        reviewSummary: this.reviewSummary, newPhotoIds: this.newPhotoIds }
    });

    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.success) {
        // isUpdated.success will be false if there is an error during any of the api calls
        if (isUpdated.businessId > 0) {
          this._router.navigateByUrl(`/business/${isUpdated.businessId}`);
        } else {
          this.reloadEntity();
          this.reloadThisEntity.emit(true);
        }
      }
    });
  }

  openEditReviewResponseDialog(review: Review) {
    if (!this._auth.authenticated || !this._auth.endUser) {
      this._auth.redirectToLogin();
      return;
    }

    let entityId: number;
    let isAirport: boolean;
    if (this.airport) {
      isAirport = true;
      entityId = this.airport.airportId;
    } else {
      isAirport = false;
      entityId = this.business.id;
    }
    const dialogRef = this.dialog.open(EditReviewResponseComponent, {
      // height: '500px',
      data: { entityId: entityId, reviewId: review.id, isAirport: isAirport, response: review.response }
    });

    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.success) {
        // isUpdated.success will be false if there is an error during any of the api calls.
        this.reloadEntity();
        this.reloadThisEntity.emit(true);
      }
    });

  }

  get entityType(): string {
    if (this.business && this.isFlyingClub) {
      return 'Flying club';
    }
    if (this.business) {
      return 'Business';
    }
    return 'Airport';
  }

  get reviews(): Array<Review> {
    if (this.business) {
      return this.business.reviews;
    } else if (this.airport) {
      return this.airport.reviews;
    }
  }

  get reviewSummary(): ReviewSummary {
    if (this.business) {
      return this.business.reviewSummary;
    } else if (this.airport) {
      return this.airport.reviewSummary;
    }
  }

  reviewImages(review: Review): NgxGalleryImage[] {
    const tempImages: NgxGalleryImage[] = [];
    review.photos.forEach( (photo) => {
      const tempImage: NgxGalleryImage = {
        small: photo.thumbnailPhotoPath,
        medium: photo.previewPhotoPath,
        big: photo.largePhotoPath,
      };
      tempImages.push(tempImage);
    });
    return tempImages;
  }

  onSortReviewsBy(sortBy: string) {
    this.sortBy = sortBy;
  }

  get sortedReviews(): Array<Review> {
    if (this.sortBy !== 'likes') {
      // sort by date
      return this.reviews
        .sort((n1, n2) => n2.addDate.getTime() - n1.addDate.getTime());
    } else {
      // sort by likes
      return this.reviews
        .sort( (n1: Review, n2: Review) => {
          if (n1.interactions.upVotes - n1.interactions.downVotes < n2.interactions.upVotes - n2.interactions.downVotes) {
            return 1;
          }
          if (n1.interactions.upVotes - n1.interactions.downVotes > n2.interactions.upVotes - n2.interactions.downVotes) {
            return -1;
          }
          if (n1.addDate.getTime() < n2.addDate.getTime()) {
            return 1;
          }
          if (n1.addDate.getTime() > n2.addDate.getTime()) {
            return -1;
          }
          return 0;
        });
    }
  }

  get hasReviewed(): boolean {
    if (!this._auth.authenticated || !this._auth.endUser || !this._userInfo) {
      return false;
    }
    return (this.reviews.findIndex(review => review.userId === Number(this._userInfo.masterCustomerId)) > -1);
  }

  isMyReview(review: Review): boolean {
    if (!this._auth.authenticated || !this._auth.endUser || !this._userInfo) {
      return false;
    }
    return (review.userId === Number(this._userInfo.masterCustomerId));
  }

  async reloadEntity() {
    if (this.business) {
      this.business = await this._airportService.getBusiness(this.business.id.toString());
    } else {
      this.airport = await this._airportService.getAirport(this.airport.identifier, true);
    }
  }

  async reportOffensive(isAirport: boolean, review: Review) {
    if (!this._auth.authenticated || !this._auth.endUser) {
      this._auth.redirectToLogin();
      return;
    }

    const dialogRef = this.dialog.open(ReviewReportOffensiveComponent, {
      width: '550px'
    });
    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        // if user confirmed delete
        const snackBarOpts = {duration: 2000};
        try {
          if (isAirport) {
            const response = await this._userService.setAirportInteraction(this.airport.airportId, review.id, 'Offensive');
          } else {
            const response = await this._userService.setBusinessInteraction(this.business.id, review.id, 'Offensive');
          }
          // this._snackBar.open(`Comment reported.`, 'Dismiss', snackBarOpts);
          this.state = State.Content;
          this.reloadEntity();
          this._snackBar.open('Thank you.  Your report has been logged and will be reviewed as soon as possible.',
            'Dismiss', {duration: 6000});
          // this.toggle.emit(this.review);
        } catch (e) {
          this._snackBar.open('An error occurred saving report. Please try again later.', 'Dismiss', snackBarOpts);
          this._errorHandler.handleError(e);

          // Set state to error, then reset after a few seconds to allow retry.
          this.state = State.Error;
          setTimeout(() => {
            this.state = State.Content;
          }, 2000);
        }
      }
    });
  }

  async removeOffensive(isAirport: boolean, review: Review) {
    if (!this._auth.authenticated || !this._auth.endUser) {
      this._auth.redirectToLogin();
      return;
    }

    const snackBarOpts = {duration: 2000};
    try {
      if (isAirport) {
        const response = await this._userService.setAirportInteraction(this.airport.airportId, review.id, 'ClearOffensive');
      } else {
        const response = await this._userService.setBusinessInteraction(this.business.id, review.id, 'ClearOffensive');
      }
      // this._snackBar.open(`Comment no longer reported.`, 'Dismiss', snackBarOpts);
      this.state = State.Content;
      this.reloadEntity();
      // this.toggle.emit(this.review);
    } catch (e) {
      this._snackBar.open('An error occurred removing your report. Please try again later.', 'Dismiss', snackBarOpts);
      this._errorHandler.handleError(e);

      // Set state to error, then reset after a few seconds to allow retry.
      this.state = State.Error;
      setTimeout(() => {
        this.state = State.Content;
      }, 2000);
    }
  }

  async clearOffensive(isAirport: boolean, review: Review) {
    if (!this._auth.authenticated || !this._auth.endUser) {
      this._auth.redirectToLogin();
      return;
    }

    const snackBarOpts = {duration: 2000};
    try {
      if (isAirport) {
        const response = await this._userService.setAirportInteraction(this.airport.airportId, review.id, 'NotOffensive');
      } else {
        const response = await this._userService.setBusinessInteraction(this.business.id, review.id, 'NotOffensive');
      }
      // this._snackBar.open(`Comment no longer reported.`, 'Dismiss', snackBarOpts);
      this.state = State.Content;
      this.reloadEntity();
      // this.toggle.emit(this.review);
    } catch (e) {
      this._snackBar.open('An error occurred clearing the user report. Please try again later.', 'Dismiss', snackBarOpts);
      this._errorHandler.handleError(e);

      // Set state to error, then reset after a few seconds to allow retry.
      this.state = State.Error;
      setTimeout(() => {
        this.state = State.Content;
      }, 2000);
    }
  }

  async deleteReview(isAirport: boolean, review: Review) {
    if (!this._auth.authenticated || !this._auth.endUser) {
      this._auth.redirectToLogin();
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '350px',
      data: 'Delete this review?'
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        // if user confirmed delete
        const snackBarOpts = {duration: 2000};
        try {
          if (isAirport) {
            const response = await this._userService.deleteAirportReview(this.airport.airportId, review.id);
          } else {
            const response = await this._userService.deleteBusinessReview(this.business.id, review.id);
          }
          // this._snackBar.open(`Comment no longer reported.`, 'Dismiss', snackBarOpts);
          this.state = State.Content;
          this.reloadEntity();
          // this.toggle.emit(this.review);
        } catch (e) {
          this._snackBar.open('An error occurred deleting the review. Please try again later.', 'Dismiss', snackBarOpts);
          this._errorHandler.handleError(e);

          // Set state to error, then reset after a few seconds to allow retry.
          this.state = State.Error;
          setTimeout(() => {
            this.state = State.Content;
          }, 2000);
        }
      }
    });
  }

  async moveReview(isAirport: boolean, review: Review) {
    if (!this._auth.authenticated || !this._auth.endUser) {
      this._auth.redirectToLogin();
      return;
    }

    let entityId: number;
    let entityType: string;
    if (this.airport) {
      entityType = 'Airport';
      entityId = this.airport.airportId;
    } else {
      entityType = 'Business';
      entityId = this.business.id;
    }

    const dialogRef = this.dialog.open(ReviewMoveComponent, {
      width: '350px',
      data: {
        entityType: entityType,
        entityId: entityId,
        reviewId: review.id
      }
    });

    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.success) {
        // isUpdated.success will be false if there is an error during any of the api calls.
        this.reloadEntity();
        this.reloadThisEntity.emit(true);
      }
    });
  }

  checkOverflow (elementID) {
    // console.log(elementID);
    const element = document.getElementById(elementID);
    if (element && (element.offsetHeight < element.scrollHeight ||
      element.offsetWidth < element.scrollWidth)) {
      return true;
    } else {
      return false;
    }
  }
}
